import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkle, CalendarCheck } from "@phosphor-icons/react";
import { Hero3D } from "@/components/Hero3D";
import { CosmicBackground } from "@/components/CosmicBackground";
import { E } from "@/components/Editable";
import { BenefitsSection } from "@/components/BenefitsSection";
import { ExpertsSection } from "@/components/ExpertsSection";
import { ScienceSection } from "@/components/ScienceSection";
import { useAuth } from "@/context/AuthContext";

const WORDS = [
  { word: "work", color: "#10B981" },
  { word: "relationships", color: "#EC4899" },
  { word: "money", color: "#F59E0B" },
  { word: "prosperity", color: "#D4AF37" },
];

export default function Landing() {
  const [idx, setIdx] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % WORDS.length), 2600);
    return () => clearInterval(id);
  }, []);

  return (
    <div data-testid="landing-page">
      <CosmicBackground />
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <Hero3D />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/60 to-transparent pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full pt-16">
          <div className="max-w-2xl">
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="text-xs uppercase tracking-[0.2em] font-bold text-gold mb-6"
            >
              <E k="hero_overline" f="78 cards · 3.6 million spreads · one you" />
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.12 }}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.05]"
              data-testid="hero-headline"
            >
              <E k="hero_title_prefix" f="The cards read your" />{" "}
              <span className="inline-block min-w-[5ch]">
                <span
                  key={WORDS[idx].word}
                  className="word-swap italic"
                  style={{ color: WORDS[idx].color }}
                >
                  {WORDS[idx].word}
                </span>
              </span>
              <br /><E k="hero_title_suffix" f="before you do." />
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-8 text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl"
            >
              <E k="hero_subtext" f="AI-guided spreads grounded in Jungian psychology, plus live 1:1 sessions with a master reader. Your readings, your history, your map forward." />
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.38 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link
                to="/book"
                data-testid="hero-book-call-button"
                className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gold text-black text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#e8c95a] transition-colors duration-300"
              >
                <CalendarCheck size={16} weight="fill" />
                <E k="hero_btn_book" f="Book a Session Now" />
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to={user ? "/reading" : "/auth"}
                data-testid="hero-draw-cards-button"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/20 text-foreground text-xs uppercase tracking-[0.2em] hover:border-gold/60 hover:text-gold transition-colors duration-300"
              >
                <Sparkle size={16} />
                <E k="hero_btn_draw" f="Draw your cards" />
              </Link>
            </motion.div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.3em] text-muted-foreground animate-pulse-glow">
          <E k="hero_scroll_hint" f="Scroll to descend" />
        </div>
      </section>

      <BenefitsSection />
      <ExpertsSection />
      <ScienceSection />

      <section className="relative py-24 lg:py-32 overflow-hidden" data-testid="cta-section">
        <div
          className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1772118451380-9ab8597f8e05?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxzYWNyZWQlMjBnZW9tZXRyeSUyMGRhcmt8ZW58MHx8fHwxNzg0MDE2Mjk5fDA&ixlib=rb-4.1.0&q=85)" }}
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tighter">
            <E k="cta_title_line1" f="The deck is shuffled." /><br /><span className="gold-text italic"><E k="cta_title_line2" f="Your move." /></span>
          </h2>
          <p className="mt-6 text-sm text-muted-foreground max-w-lg mx-auto leading-relaxed">
            <E k="cta_subtext" f="Create a free account, draw your first AI-guided spread in seconds, and keep every reading in your private archive." />
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/book"
              data-testid="cta-book-button"
              className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-gold text-black text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#e8c95a] transition-colors duration-300"
            >
              <CalendarCheck size={16} weight="fill" /> <E k="cta_book_button" f="Book a Live Session" />
            </Link>
            <Link
              to={user ? "/reading" : "/auth"}
              data-testid="cta-begin-button"
              className="inline-flex items-center gap-2 px-10 py-4 rounded-full border border-gold/50 text-gold text-xs uppercase tracking-[0.2em] font-bold hover:bg-gold hover:text-black transition-colors duration-300"
            >
              <E k="cta_button" f="Begin your reading" /> <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-10 px-6 lg:px-10" data-testid="site-footer">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-heading text-xl font-semibold tracking-tighter"><E k="footer_brand" f="ARCANA" /></p>
          <Link
            to="/book"
            data-testid="footer-book-link"
            className="text-[10px] uppercase tracking-[0.2em] text-gold hover:underline"
          >
            <E k="footer_book_link" f="Book a session →" />
          </Link>
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <E k="footer_disclaimer" f="For reflection & entertainment · not medical, legal or financial advice" />
          </p>
        </div>
      </footer>
    </div>
  );
}
